import { Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { PermissionService } from '../../../core/services/permission.service';
import { DashboardConfigService } from './dashboard-config.service';
import { WidgetHostComponent } from './widget-host.component';
import { WidgetDescriptor } from './dashboard-widget.model';

@Component({
  selector: 'app-dashboard-grid',
  standalone: true,
  imports: [WidgetHostComponent],
  template: `
    <div class="flex flex-col gap-4">
      @if (tiles().length) {
      <section class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        @for (w of tiles(); track w.id) {
        <app-widget-host [widget]="w" />
        }
      </section>
      }

      @if (left().length || right().length) {
      <!-- Two independent columns; each card sizes to its own content. -->
      <div class="flex flex-col gap-4 lg:flex-row">
        @if (left().length) {
        <div class="flex flex-1 flex-col gap-4">
          @for (w of left(); track w.id) {
          <app-widget-host [widget]="w" />
          }
        </div>
        }
        @if (right().length) {
        <div class="flex flex-1 flex-col gap-4">
          @for (w of right(); track w.id) {
          <app-widget-host [widget]="w" />
          }
        </div>
        }
      </div>
      }

      @if (full().length) {
      <!-- Full-width row(s) spanning the whole dashboard, below the columns. -->
      @for (w of full(); track w.id) {
      <app-widget-host [widget]="w" />
      }
      }
    </div>
  `,
})
export class DashboardGridComponent {
  private config = inject(DashboardConfigService);
  private permissions = inject(PermissionService);

  private all = toSignal(this.config.getWidgets(), { initialValue: [] as WidgetDescriptor[] });

  /** Widgets the current user may see, permission-filtered, in layout order. */
  private visible = computed(() =>
    this.all()
      .filter((w) => !w.permission || this.permissions.has(w.permission))
      .sort((a, b) => a.layout.order - b.layout.order),
  );

  tiles = computed(() => this.visible().filter((w) => (w.layout.region ?? 'tiles') === 'tiles'));
  left = computed(() => this.visible().filter((w) => w.layout.region === 'left'));
  right = computed(() => this.visible().filter((w) => w.layout.region === 'right'));
  full = computed(() => this.visible().filter((w) => w.layout.region === 'full'));
}
